<script setup lang="ts">
import WarningIcon from '@/components/base/assets/WarningIcon.vue';

const props = withDefaults(
  defineProps<{
    cols?: number;
    dataTest?: string;
    errorMessage?: string;
    fullWidth?: boolean;
    id?: string;
    label?: string;
    labelSROnly?: boolean;
    maxLength?: number;
    name: string;
    placeholder?: string;
    resize?: boolean;
    rows?: number;
  }>(),
  {
    cols: 20,
    fullWidth: true,
    labelSROnly: false,
    resize: false,
    rows: 6,
  },
);
const currentValue = defineModel<string>({
  set: (text) => (props.maxLength ? text?.slice(0, props.maxLength) : text),
});
</script>

<template>
  <div>
    <slot name="label">
      <span
        v-if="label"
        :class="labelSROnly ? 'sr-only' : 'inline-block mb-2 text-gray-700 form-label'"
        :id="id ? `label-${id}` : undefined"
      >
        {{ label }}
        <span v-if="errorMessage" class="sr-only">, {{ errorMessage }}</span>
        <span class="sr-only">, </span>
      </span>
    </slot>
    <div class="relative">
      <textarea
        v-model="currentValue"
        :aria-labelledby="id && label ? `label-${id}` : undefined"
        :aria-invalid="!!errorMessage"
        class="box-border px-2 py-3 text-base leading-5 border border-solid font-proxima-nova focus:border-black focus:outline-none"
        :class="[
          !!errorMessage ? 'border-nuts-red-800 placeholder-red-700' : 'border-neutral-300',
          {
            'w-full': fullWidth,
            'resize-none': !resize,
          },
          !!errorMessage ? 'border-nuts-red-800' : 'border-neutral-300',
        ]"
        :cols="cols"
        :id="id"
        :maxlength="maxLength"
        :name="name"
        :placeholder="placeholder"
        :rows="rows"
      />
      <WarningIcon
        v-if="!!errorMessage"
        class="absolute right-4 top-3 text-nuts-red-800"
        aria-hidden="true"
        :data-test="`${dataTest}-${name}-field-error-icon`"
        key="fieldError"
      />
    </div>
    <div class="flex justify-between">
      <transition name="fade">
        <div
          v-if="!!errorMessage"
          class="text-xs leading-3 text-nuts-red-800"
          :id="`inputHelperText${name ? '-' + name : ''}`"
          role="alert"
          :data-test="`${dataTest}-${name}-validation-error`"
        >
          {{ errorMessage }}
        </div>
      </transition>
      <div v-if="maxLength" class="ml-auto text-sm text-right">
        {{ currentValue?.length ?? 0 }} / {{ maxLength }} characters
      </div>
    </div>
  </div>
</template>
